/**
 * Consent-gated Meta advertising measurement.
 *
 * It shares GA4's consent posture exactly, because a second tag with its own
 * rules is how one of them ends up running when the teacher said no:
 *   - student accounts are never measured, in any region, under any consent;
 *   - EU/UK visitors must opt in;
 *   - everyone else may opt out at any time, and an already-running tag stops.
 *
 * The browser side never enables Advanced Matching and never sends a name, an
 * email, an account id, class data or anything belonging to a student. The
 * only identifiers Meta ever receives from us are one-way hashes attached to a
 * completed teacher subscription, sent server-side by the Stripe webhook after
 * Stripe confirms the payment — see netlify/functions/shared/metaConversions.ts.
 *
 * Advertising measurement is the least important thing this product does, so
 * every function here fails silently. Nothing in this module may break a
 * teacher's flow.
 */

const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID || '').trim();

type MetaPixelFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: MetaPixelFunction;
  loaded?: boolean;
  version?: string;
};

type MetaWindow = Window & {
  fbq?: MetaPixelFunction;
  _fbq?: MetaPixelFunction;
};

let injected = false;
let enabled = false;

function getMetaWindow(): MetaWindow | null {
  try {
    return typeof window === 'undefined' ? null : (window as MetaWindow);
  } catch {
    return null;
  }
}

/** Meta's standard queue stub, so events fired before the script lands survive. */
function ensureFbq(w: MetaWindow): MetaPixelFunction {
  if (w.fbq) return w.fbq;

  const fbq = function metaPixelQueue(...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue!.push(args);
  } as MetaPixelFunction;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  w.fbq = fbq;
  w._fbq = fbq;
  return fbq;
}

function injectMetaPixel(): void {
  if (injected || !META_PIXEL_ID || typeof document === 'undefined') return;
  const w = getMetaWindow();
  if (!w) return;
  injected = true;

  const fbq = ensureFbq(w);
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.id = 'luc-meta-pixel';
  document.head.appendChild(script);

  fbq('consent', 'grant');
  fbq('init', META_PIXEL_ID);
  fbq('track', 'PageView');
}

/** Enable measurement and load the tag, if one is configured. */
export function enableMetaPixel(): void {
  if (!META_PIXEL_ID) return;
  enabled = true;
  const w = getMetaWindow();
  if (w?.fbq) w.fbq('consent', 'grant');
  injectMetaPixel();
}

/**
 * Revoke consent immediately, including for a tag that is already running.
 *
 * Opting out mid-session has to stop a LOADED tag, not merely skip a future
 * injection — the same reason analytics.ts actively disables GA4 rather than
 * returning early.
 */
export function disableMetaPixel(): void {
  enabled = false;
  const w = getMetaWindow();
  if (w?.fbq) w.fbq('consent', 'revoke');
}

/** Fire a Meta standard event. No-op unless consent is active. */
export function trackMetaEvent(name: string, params?: Record<string, unknown>): void {
  if (!enabled) return;
  try {
    const fbq = getMetaWindow()?.fbq;
    if (typeof fbq === 'function') fbq('track', name, params || {});
  } catch {
    // Advertising measurement must never break a real flow.
  }
}

/**
 * Context attached to a teacher's Stripe Checkout Session.
 *
 * Consent is captured at the MOMENT CHECKOUT BEGINS and carried on the session,
 * so the webhook — which may fire minutes later, from Stripe, with no browser
 * involved — can honour the choice the teacher had made at the time. Without
 * this the server would have no way to know, and would either send everything
 * or nothing.
 *
 * Returns no user agent when consent is absent, so there is nothing to attach.
 */
export function getMetaConversionContext(): {
  consentGranted: boolean;
  clientUserAgent?: string;
} {
  if (!enabled) return { consentGranted: false };
  let clientUserAgent: string | undefined;
  try {
    // Strip control characters only. Stripe metadata must be clean text,
      // but a broader class would mangle the user agent — spaces, dots and
      // slashes are all load-bearing in a UA string.
      // eslint-disable-next-line no-control-regex
      clientUserAgent = navigator.userAgent.replace(/[\u0000-\u001F\u007F]/g, '').slice(0, 500);
  } catch {
    // Unavailable — the Purchase still sends on the hashed identifiers.
  }
  return { consentGranted: true, ...(clientUserAgent ? { clientUserAgent } : {}) };
}

/** Test seam: resets module state between cases. Not used by the app. */
export function __resetMetaPixelForTests(): void {
  injected = false;
  enabled = false;
}
